import { apiClient } from './apiClient';

// Template type constants - aligned with backend constants
export const TEMPLATE_TYPE_ADMIN = '2';
export const TEMPLATE_TYPE_FLEET_MANAGER = '1';

export interface GCSInfo {
  downloadUrl: string;
  mimetype: string;
  name: string;
  path: string;
  publicUrl: string;
  size: number;
}

export interface UploadedFileInfo {
  id: string;
  adminId?: string;
  fleetManagerId?: string;
  uploadedByEmail: string;
  uploadedByName: string;
  associatedEntityId: string;
  associatedEntityType: string;
  uploadedFrom: number; // 1 = Client, 2 = Admin
  uploadedType: number; // 1 = Raw, 2 = Formatted
  rawUploadId?: string; // For formatted files - links to raw upload
  formattedUploadId?: string; // For raw files - links to formatted upload
  status: string; // uploaded, processing, approved, completed, failed
  gcsInfo: GCSInfo;
  totalRows: number; // Total voyages processed
  successfulRows: number; // Successfully processed voyages
  failedRows: number; // Failed voyage processing
  createdAt: string; // Formatted date-time string
  updatedAt: string; // Formatted date-time string
}

export interface VesselUploadGroup {
  vesselId: string;
  vesselImo: string;
  vesselName: string;
  uploadedFileInfo: UploadedFileInfo[];
}

export interface UploadFormattedFileResponse {
  uploadId: string;
  downloadUrl: string;
  publicURL: string;
  gcsPath: string;
  status: string;
}

export interface ApproveFormattedFileRequest {
  vessel_id: string;
  uploaded_type: number; // 2 = Formatted
  uploaded_from: number; // 2 = Admin
  status: string; // 'approved'
  comments: string;
}

export interface ApproveFormattedFileResponse {
  message: string;
  status: string;
}

export interface ValidationError {
  row: number;
  field: string;
  value: string;
  error: string;
  error_type: number; // 1 = column-level, 2 = row-level
  message: string;
}

export interface AtomicUploadResponse {
  success: boolean;
  upload_id?: string;
  download_url?: string;
  gcs_path?: string;
  created_voyage_ids?: string[];
  validation_errors?: ValidationError[];
  error_message?: string;
  error_step?: number; // 0=File Upload, 1=Parse & Validate, 2=Voyages Created
}

export class XlsService {
  /**
   * Get all XLS uploads grouped by vessel
   */
  static async getVesselUploadsList(): Promise<VesselUploadGroup[]> {
    return apiClient.post<VesselUploadGroup[]>('/xls/vessel/list', {});
  }

  /**
   * Upload formatted XLS file for a vessel
   */
  static async uploadFormattedFile(
    vesselId: string,
    vesselImo: string,
    vesselName: string,
    file: File,
    rawUploadId: string
  ): Promise<UploadFormattedFileResponse> {
    const formData = new FormData();
    formData.append('vessel_id', vesselId);
    formData.append('vessel_imo', vesselImo);
    formData.append('vessel_name', vesselName);
    formData.append('file', file);
    formData.append('upload_from', '2'); // Admin
    formData.append('upload_type', '2'); // Formatted
    formData.append('raw_upload_id', rawUploadId); // Link to raw upload

    return apiClient.postFormData<UploadFormattedFileResponse>('/xls/raw/upload', formData);
  }

  /**
   * Approve formatted XLS file to create voyages
   */
  static async approveFormattedFile(
    vesselId: string,
    comments: string
  ): Promise<ApproveFormattedFileResponse> {
    const payload: ApproveFormattedFileRequest = {
      vessel_id: vesselId,
      uploaded_type: 2, // Formatted
      uploaded_from: 2, // Admin
      status: 'approved',
      comments,
    };

    return apiClient.post<ApproveFormattedFileResponse>('/xls/create/voyages', payload);
  }

  /**
   * Atomic upload: validate, upload to GCS, and create voyages in one request
   */
  static async uploadAndCreateVoyages(
    vesselId: string,
    vesselImo: string,
    vesselName: string,
    clientId: string,
    file: File,
    rawUploadId?: string,
    chartererId?: string
  ): Promise<AtomicUploadResponse> {
    const formData = new FormData();
    formData.append('vessel_id', vesselId);
    formData.append('vessel_imo_number', vesselImo);
    formData.append('vessel_name', vesselName);
    formData.append('charterer_id', chartererId || ''); // Optional
    formData.append('client_id', clientId);
    formData.append('uploaded_from', '2'); // Admin
    formData.append('file', file);
    if (rawUploadId) {
      formData.append('raw_upload_id', rawUploadId);
    }

    return apiClient.postFormData<AtomicUploadResponse>('/xls/upload-and-create-voyages', formData);
  }

  /**
   * Delete a formatted XLS file by document ID
   */
  static async deleteFormattedFile(
    documentId: string
  ): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>('/xls/delete-upload', {
      document_id: documentId,
      file_uploaded_type: 2,
    });
  }

  /**
   * Delete a raw XLS file by document ID
   */
  static async deleteRawFile(documentId: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>('/xls/delete-upload', {
      document_id: documentId,
      file_uploaded_type: 1,
    });
  }

  /**
   * Delete a formatted XLS file and its associated voyages by document ID
   */
  static async deleteFormattedFileAndVoyages(
    documentId: string
  ): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>('/xls/delete-upload', {
      document_id: documentId,
      file_uploaded_type: 2,
      delete_voyages: true, // The new parameter
    });
  }

  /**
   * Download admin template - returns blob and filename for file download
   */
  static async downloadAdminTemplate(): Promise<{ blob: Blob; fileName?: string }> {
    try {
      const result = await apiClient.getBlob(`/xls/template?type=${TEMPLATE_TYPE_ADMIN}`);
      return result;
    } catch (error) {
      console.error('Error downloading admin template:', error);
      throw error;
    }
  }

  /**
   * Get fresh download URL for an uploaded file
   * This generates a new signed URL that is always valid
   */
  static async getFreshDownloadUrl(
    uploadId: string
  ): Promise<{ downloadUrl: string; fileName: string }> {
    try {
      const result = await apiClient.get<{ downloadUrl: string; fileName: string }>(
        `/xls/download/${uploadId}`
      );
      return result;
    } catch (error) {
      console.error('Error getting fresh download URL:', error);
      throw error;
    }
  }
}
